import { Box, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import AddContactModal from "components/chat/AddContactModal";
import ChatInput from "components/chat/ChatInput";
import ChatWindowHeader from "components/chat/ChatWindowHeader";
import EmptyMessage from "components/chat/EmptyMessage";
import FileProgress from "components/chat/FileProgress";
import Message from "components/chat/Message";
import TypingIndicator from "components/chat/TypingIndicator";
import { useToast } from "contexts/ToastContext";
import { insertDateSeparators, isDateSeparator } from "helpers";
import {
  FirebaseStorageService,
  UploadProgress,
} from "helpers/firebaseStorage";
import {
  useAddContact,
  useCheckContactExist,
  useConversationHistory,
  useUserPresenceStatus,
} from "hooks/useConversations";
import { useConversationSocket } from "hooks/useConversationSocket";
import {
  AddContactDtoContactTypeEnum,
  ConversationDto,
  UserPresenceResponseDtoStatusEnum,
} from "libs/cellera-sdk";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useCelleraTheme } from "theme/useCelleraTheme";
import { Patient } from "types";
import { ChatMessage, MessageType, SenderType } from "types/socket";
import { formatPatientName } from "utils/patient";

type ChatWindowProps = {
  conversation: ConversationDto;
};

const SCROLL_THROTTLE_MS = 100;
const SCROLL_NEAR_BOTTOM_THRESHOLD = 50;
const SCROLL_LOAD_MORE_THRESHOLD = 100;

const ChatWindow: React.FC<ChatWindowProps> = ({
  conversation,
}: ChatWindowProps) => {
  const { colors } = useCelleraTheme();
  const { showError } = useToast();
  const {
    messages,
    isPatientTyping,
    sendMessage,
    sendTyping,
    markMessageAsRead,
  } = useConversationSocket(conversation.id);
  const {
    data: messagesHistory,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingMessages,
  } = useConversationHistory(conversation.id);
  const { data: userPresence } = useUserPresenceStatus(
    conversation.patient.patientId
  );
  const { mutateAsync: addContact, isPending: isAddingContact } =
    useAddContact();
  const { data: contactExist, refetch: refetchContactExist } =
    useCheckContactExist(conversation.patient.patientId);
  const queryClient = useQueryClient();

  const [message, setMessage] = useState<string>("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<
    Map<string, { file: File; progress: UploadProgress }>
  >(new Map());
  const [addContactModalOpen, setAddContactModalOpen] = useState(false);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const isInitialMountRef = useRef(true);
  const isLoadingMoreRef = useRef(false);
  const previousScrollHeightRef = useRef<number>(0);
  const shouldScrollToBottomRef = useRef(true);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const conversationIdRef = useRef<string>(conversation.id);
  const sentReadRef = useRef<Set<string>>(new Set());

  // Memoize history messages - only recalculate when pages change
  const history = useMemo(() => {
    if (!messagesHistory?.pages) return [];
    return messagesHistory.pages.flatMap((page) => page.messages).reverse();
  }, [messagesHistory?.pages]);

  // Merge and transform messages efficiently
  const allMessages = useMemo(() => {
    const transformedHistory: ChatMessage[] = history.map((msg) => ({
      ...(msg as ChatMessage),
      senderType: msg.sender?.userType as SenderType,
    }));
    return [...transformedHistory, ...messages];
  }, [history, messages]);

  const hasMessages = useMemo(() => allMessages.length > 0, [allMessages]);

  // Insert date separators
  const messagesWithSeparators = useMemo(() => {
    return insertDateSeparators(allMessages);
  }, [allMessages]);

  // Get the last unread message for the patient
  const lastUnreadMessages = useMemo(() => {
    return allMessages.filter(
      (msg) => msg.senderType === SenderType.PATIENT && !msg.readAt
    );
  }, [allMessages]);

  // Memoize styles to prevent recreation on every render
  const containerStyles = useMemo(
    () => ({
      flex: 1,
      height: "100%",
      display: "flex",
      flexDirection: "column" as const,
    }),
    []
  );

  const scrollContainerStyles = useMemo(
    () => ({
      flex: 1,
      backgroundColor: colors.background.grayLight,
      overflowY: "scroll" as const,
    }),
    [colors.background.grayLight]
  );

  const messagesContainerStyles = useMemo(
    () => ({
      padding: 2,
      display: "flex",
      flexDirection: "column",
      gap: 2,
      justifyContent: hasMessages ? "flex-end" : "center",
      height: !hasMessages ? "100%" : "auto",
    }),
    [hasMessages]
  );

  const canAddContact = useMemo(() => {
    return contactExist && !contactExist?._exists;
  }, [contactExist]);

  const handleOpenAddContactModal = useCallback(() => {
    if (canAddContact) {
      setAddContactModalOpen((prev) => !prev);
    }
  }, [canAddContact]);

  const handleAddContact = useCallback(async () => {
    if (conversation.patient.patientId) {
      try {
        await addContact({
          addContactDto: {
            contactId: conversation.patient.patientId,
            contactType: AddContactDtoContactTypeEnum.Patient,
          },
        });
        refetchContactExist();
      } catch (error: unknown) {
        showError(
          "Error",
          error instanceof Error ? error.message : "Unknown error"
        );
      } finally {
        setAddContactModalOpen(false);
      }
    }
  }, [
    addContact,
    conversation.patient.patientId,
    refetchContactExist,
    showError,
  ]);

  // Optimized scroll to bottom
  const scrollToBottom = useCallback(() => {
    scrollContainerRef.current?.scrollTo({
      top: scrollContainerRef.current.scrollHeight,
      behavior: "auto",
    });
  }, []);

  const handleChangeMessage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextValue = event.target.value;
      // Send typing indicator when user starts typing
      if (message.length === 0 && nextValue.length > 0) {
        sendTyping({ conversationId: conversation.id });
      }
      setMessage(nextValue);
    },
    [conversation.id, message.length, sendTyping]
  );

  const handleFileSelect = useCallback((file: File) => {
    setSelectedFiles((prev) => [...prev, file]);
  }, []);

  const handleRemoveFile = useCallback((index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSendMessage = useCallback(async () => {
    const trimmedMessage = message.trim();
    const hasFiles = selectedFiles.length > 0;

    if (!trimmedMessage && !hasFiles) return;

    // If there are files, upload them first
    if (hasFiles) {
      // Save files and message before clearing
      const filesToUpload = [...selectedFiles];
      const messageToSend = trimmedMessage;

      // Clear files and message immediately when upload starts
      setSelectedFiles([]);
      setMessage("");

      try {
        const uploadPromises = filesToUpload.map(async (file, index) => {
          const fileId = `${file.name}-${index}-${Date.now()}`;

          // Initialize progress
          setUploadingFiles((prev) => {
            const newMap = new Map(prev);
            newMap.set(fileId, {
              file,
              progress: {
                bytesTransferred: 0,
                totalBytes: file.size,
                progress: 0,
              },
            });
            return newMap;
          });

          try {
            const folder = `chat-files/${conversation.id}`;
            const downloadURL =
              await FirebaseStorageService.uploadFileWithProgress(
                file,
                {
                  folder,
                  fileName: file.name,
                  generateUniqueFileName: true,
                },
                (progress) => {
                  setUploadingFiles((prev) => {
                    const newMap = new Map(prev);
                    const existing = newMap.get(fileId);
                    if (existing) {
                      newMap.set(fileId, {
                        file: existing.file,
                        progress,
                      });
                    }
                    return newMap;
                  });
                }
              );

            // Remove from uploading files
            setUploadingFiles((prev) => {
              const newMap = new Map(prev);
              newMap.delete(fileId);
              return newMap;
            });

            return {
              url: downloadURL,
              filename: file.name,
              mimeType: file.type,
              size: file.size,
              thumbnail: file.type.startsWith("image/")
                ? downloadURL
                : undefined,
            };
          } catch (error) {
            // Remove from uploading files on error
            setUploadingFiles((prev) => {
              const newMap = new Map(prev);
              newMap.delete(fileId);
              return newMap;
            });
            throw error;
          }
        });

        const attachments = await Promise.all(uploadPromises);

        // Determine message type based on attachments
        const messageType =
          attachments.length === 1 &&
          attachments[0].mimeType.startsWith("image/")
            ? MessageType.IMAGE
            : MessageType.FILE;

        // Send message with attachments
        sendMessage({
          conversationId: conversation.id,
          content: messageToSend || undefined,
          messageType,
          attachments,
        });
      } catch (error) {
        console.error("Error uploading files:", error);
        showError("Upload failed", "Failed to upload files. Please try again.");
        // Clear uploading state
        setUploadingFiles(new Map());
      }
    } else {
      // Send text message only
      sendMessage({
        conversationId: conversation.id,
        content: trimmedMessage,
      });
      setMessage("");
    }

    shouldScrollToBottomRef.current = true;
    requestAnimationFrame(scrollToBottom);
  }, [
    conversation.id,
    message,
    selectedFiles,
    sendMessage,
    scrollToBottom,
    showError,
  ]);

  // Optimized scroll handler with throttling
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Clear existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Throttle scroll handling
    scrollTimeoutRef.current = setTimeout(() => {
      const currentContainer = scrollContainerRef.current;
      if (!currentContainer) return;

      const { scrollTop, scrollHeight, clientHeight } = currentContainer;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      // Update auto-scroll flag
      shouldScrollToBottomRef.current =
        distanceFromBottom < SCROLL_NEAR_BOTTOM_THRESHOLD;

      // Load more messages when near top
      if (
        scrollTop < SCROLL_LOAD_MORE_THRESHOLD &&
        hasNextPage &&
        !isFetchingNextPage &&
        !isLoadingMoreRef.current
      ) {
        isLoadingMoreRef.current = true;
        previousScrollHeightRef.current = scrollHeight;

        fetchNextPage()
          .then(() => {
            // Maintain scroll position after loading
            requestAnimationFrame(() => {
              const updatedContainer = scrollContainerRef.current;
              if (updatedContainer && previousScrollHeightRef.current > 0) {
                const newScrollHeight = updatedContainer.scrollHeight;
                const heightDifference =
                  newScrollHeight - previousScrollHeightRef.current;

                if (heightDifference > 0) {
                  updatedContainer.scrollTop += heightDifference;
                }
                previousScrollHeightRef.current = 0;
              }
            });
          })
          .finally(() => {
            isLoadingMoreRef.current = false;
          });
      }
    }, SCROLL_THROTTLE_MS);
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Handle conversation change
  useEffect(() => {
    if (conversationIdRef.current !== conversation.id) {
      isInitialMountRef.current = true;
      shouldScrollToBottomRef.current = true;
      conversationIdRef.current = conversation.id;
      setMessage("");
      setSelectedFiles([]);
      setUploadingFiles(new Map());
    }
  }, [conversation.id]);

  // Consolidated scroll management
  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Initial scroll when history loads
    if (isInitialMountRef.current && history.length > 0) {
      requestAnimationFrame(() => {
        scrollToBottom();
        isInitialMountRef.current = false;
        shouldScrollToBottomRef.current = true;
      });
      return;
    }

    // Auto-scroll for new socket messages
    if (
      messages.length > 0 &&
      shouldScrollToBottomRef.current &&
      !isInitialMountRef.current
    ) {
      requestAnimationFrame(scrollToBottom);
    }
  }, [conversation.id, history.length, messages.length, scrollToBottom]);

  useLayoutEffect(() => {
    if (typeof ResizeObserver === "undefined") return;

    const messagesContainer = messagesContainerRef.current;
    if (!messagesContainer) return;

    const observer = new ResizeObserver(() => {
      if (shouldScrollToBottomRef.current) {
        requestAnimationFrame(scrollToBottom);
      }
    });

    observer.observe(messagesContainer);

    return () => {
      observer.disconnect();
    };
  }, [scrollToBottom]);

  // Scroll to bottom when files are uploading
  useLayoutEffect(() => {
    if (uploadingFiles.size > 0) {
      requestAnimationFrame(scrollToBottom);
    }
  }, [uploadingFiles.size, scrollToBottom]);

  useEffect(() => {
    if (!lastUnreadMessages.length) return;

    const newUnreadToMark = lastUnreadMessages.filter(
      (msg) => !sentReadRef.current.has(msg.id)
    );

    if (!newUnreadToMark.length) return;

    newUnreadToMark.forEach((message) => {
      sentReadRef.current.add(message.id);
      markMessageAsRead(message.id);
    });

    queryClient.invalidateQueries({
      queryKey: ["conversations"],
    });
  }, [lastUnreadMessages, markMessageAsRead, queryClient]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Box sx={containerStyles}>
      {/* Chat Header */}
      <ChatWindowHeader
        patient={conversation.patient}
        presenceStatus={
          userPresence?.status ?? UserPresenceResponseDtoStatusEnum.Offline
        }
        onAddContact={canAddContact ? handleOpenAddContactModal : undefined}
      />

      {/* Messages Area */}
      <Box
        ref={scrollContainerRef}
        onScroll={handleScroll}
        sx={scrollContainerStyles}
      >
        <Box ref={messagesContainerRef} sx={messagesContainerStyles}>
          {hasMessages ? (
            messagesWithSeparators.map((item) => {
              // Render date separator
              if (isDateSeparator(item)) {
                return (
                  <Box
                    key={item.id}
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      mt: 2,
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.text.secondary,
                        fontSize: "12px",
                        fontWeight: 500,
                      }}
                    >
                      {item.dateLabel}
                    </Typography>
                  </Box>
                );
              }

              // Render message (type guard ensures this is ChatMessage)
              const message = item as ChatMessage;
              return (
                <Message
                  key={message.id}
                  message={message}
                  conversation={conversation}
                />
              );
            })
          ) : (
            <EmptyMessage isLoading={isLoadingMessages} />
          )}

          {/* Typing Indicator */}
          {isPatientTyping && (
            <TypingIndicator
              senderName={formatPatientName(conversation.patient as Patient)}
            />
          )}

          {/* Upload Progress Area */}
          {uploadingFiles.size > 0 && (
            <Box
              sx={{
                px: 2,
                py: 1,
                backgroundColor: colors.background.paper,
                borderTop: `1px solid ${colors.border.light}`,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                maxHeight: "200px",
                overflowY: "auto",
              }}
            >
              {Array.from(uploadingFiles.entries()).map(
                ([fileId, { file, progress }]) => {
                  return (
                    <FileProgress
                      key={fileId}
                      fileName={file.name}
                      progress={progress.progress}
                      bytesTransferred={progress.bytesTransferred}
                      totalBytes={progress.totalBytes}
                    />
                  );
                }
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Message Input Area */}
      <ChatInput
        message={message}
        onChange={handleChangeMessage}
        onSend={handleSendMessage}
        onFileSelect={handleFileSelect}
        selectedFiles={selectedFiles}
        onRemoveFile={handleRemoveFile}
      />

      <AddContactModal
        open={addContactModalOpen}
        isAdding={isAddingContact}
        onAdd={handleAddContact}
        onClose={() => setAddContactModalOpen(false)}
      />
    </Box>
  );
};

export default ChatWindow;
